import React, { useState } from 'react';
import { ShoppingCartIcon, TrendingUpIcon, CalendarIcon, PackageIcon, BarChart3Icon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { StatsCard } from '../components/ui/StatsCard';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { Pagination } from '../components/ui/Pagination';
import { Select } from '../components/ui/Select';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
export function SalesPage() {
  const {
    user
  } = useAuth();
  const {
    visits
  } = useApp();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const [dateFilter, setDateFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 6;
  const salesVisits = visits.filter(v => isAdmin || v.vendedorId === user?.id).filter(v => v.venta.huboVenta).filter(v => {
    if (dateFilter) {
      const visitDate = new Date(v.fecha).toISOString().split('T')[0];
      if (visitDate !== dateFilter) return false;
    }
    return true;
  }).sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());
  const totalVendido = salesVisits.reduce((sum, v) => sum + v.venta.montoVenta, 0);
  const totalPedidos = salesVisits.length;
  const promedioVenta = totalPedidos > 0 ? totalVendido / totalPedidos : 0;
  const today = new Date();
  const todaySales = salesVisits.filter(v => new Date(v.fecha).toDateString() === today.toDateString());
  const todayTotal = todaySales.reduce((sum, v) => sum + v.venta.montoVenta, 0);
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0
  }).format(amount);
  const formatDate = (date: Date) => new Date(date).toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'short'
  });
  const totalPages = Math.ceil(salesVisits.length / ITEMS_PER_PAGE);
  const paginatedSales = salesVisits.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const salesByVendor = isAdmin ? salesVisits.reduce((acc, v) => {
    if (!acc[v.vendedorNombre]) acc[v.vendedorNombre] = {
      total: 0,
      count: 0
    };
    acc[v.vendedorNombre].total += v.venta.montoVenta;
    acc[v.vendedorNombre].count += 1;
    return acc;
  }, {} as Record<string, {
    total: number;
    count: number;
  }>) : {};
  return <div className="space-y-6" data-id="element-3498">
      <PageHeader title="Registro de Ventas" subtitle="Historial de ventas y facturación" icon={<ShoppingCartIcon className="w-6 h-6" data-id="element-3500" />} data-id="element-3499">
        <div className="flex items-center gap-2 w-full sm:w-auto" data-id="element-3501">
          <Input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} leftIcon={<CalendarIcon className="w-4 h-4" data-id="element-3503" />} data-id="element-3502" />
          {dateFilter && <Button variant="secondary" onClick={() => setDateFilter('')} data-id="element-3504">
              Limpiar
            </Button>}
        </div>
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-stagger-1" data-id="element-3505">
        <StatsCard title="Total Vendido" value={formatCurrency(totalVendido)} icon={<ShoppingCartIcon className="w-5 h-5" data-id="element-3507" />} variant="primary" data-id="element-3506" />
        <StatsCard title="Ventas Hoy" value={formatCurrency(todayTotal)} subtitle={`${todaySales.length} pedidos`} icon={<TrendingUpIcon className="w-5 h-5" data-id="element-3509" />} variant="success" data-id="element-3508" />
        <StatsCard title="Total Pedidos" value={totalPedidos} icon={<PackageIcon className="w-5 h-5" data-id="element-3511" />} variant="warning" data-id="element-3510" />
        <StatsCard title="Promedio por Venta" value={formatCurrency(promedioVenta)} icon={<BarChart3Icon className="w-5 h-5" data-id="element-3513" />} variant="default" data-id="element-3512" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3514">
        {/* Sales list */}
        <Card padding="none" className="animate-stagger-2" data-id="element-3515">
          <div className="px-6 py-5 border-b border-slate-100" data-id="element-3516">
            <CardHeader title="Historial de Ventas" subtitle={`${salesVisits.length} ventas registradas`} data-id="element-3517" />
          </div>

          {salesVisits.length === 0 ? <div className="p-16 text-center animate-fade-in" data-id="element-3518">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-3519">
                <ShoppingCartIcon className="w-8 h-8 text-slate-400" data-id="element-3520" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-3521">
                No hay ventas
              </h3>
              <p className="text-slate-500 text-sm" data-id="element-3522">
                No hay ventas registradas en el período seleccionado
              </p>
            </div> : <>
              <div className="divide-y divide-slate-100" data-id="element-3523">
                {paginatedSales.map(visit => {
              const montoTotal = visit.venta.montoVenta;
              const subTotal = Math.round(montoTotal / 1.18 * 100) / 100;
              const itbis = Math.round((montoTotal - subTotal) * 100) / 100;
              return <div key={visit.id} className="px-6 py-4 hover:bg-slate-50/50 transition-colors" data-id="element-3524">
                      <div className="flex items-start justify-between" data-id="element-3525">
                        <div className="flex-1" data-id="element-3526">
                          <div className="flex items-center gap-3" data-id="element-3527">
                            <h4 className="font-semibold text-slate-900" data-id="element-3528">
                              {visit.clienteNombre}
                            </h4>
                            <Badge variant="info" data-id="element-3529">
                              {visit.venta.numeroPedido}
                            </Badge>
                          </div>
                          <div className="mt-1 flex items-center gap-4 text-sm text-slate-500" data-id="element-3530">
                            {isAdmin && <span className="font-medium" data-id="element-3531">
                                {visit.vendedorNombre}
                              </span>}
                            <span data-id="element-3532">{formatDate(visit.fecha)}</span>
                            <span data-id="element-3533">{visit.zona}</span>
                          </div>
                          {visit.comentarios && <p className="mt-2 text-sm text-slate-600" data-id="element-3534">
                              {visit.comentarios}
                            </p>}
                        </div>
                        <div className="text-right flex-shrink-0 ml-4" data-id="element-3535">
                          <p className="text-xl font-bold text-[#2952C4]" data-id="element-3536">
                            {formatCurrency(montoTotal)}
                          </p>
                          <div className="mt-1 text-xs text-slate-500 space-y-0.5" data-id="element-3537">
                            <p data-id="element-3538">
                              Sub Total:{' '}
                              <span className="font-semibold text-slate-700" data-id="element-3539">
                                {formatCurrency(subTotal)}
                              </span>
                            </p>
                            <p data-id="element-3540">
                              ITBIS 18%:{' '}
                              <span className="font-semibold text-slate-600" data-id="element-3541">
                                {formatCurrency(itbis)}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>;
            })}
              </div>
              <div className="animate-stagger-3" data-id="element-3542">
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={salesVisits.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-3543" />
              </div>
            </>}
        </Card>

        {/* Sales by vendor (admin only) */}
        {isAdmin && <Card data-id="element-3544">
            <CardHeader title="Ventas por Vendedor" data-id="element-3545" />
            <div className="mt-4 space-y-4" data-id="element-3546">
              {Object.entries(salesByVendor).sort((a, b) => b[1].total - a[1].total).map(([vendedor, data]) => <div key={vendedor} className="p-4 bg-slate-50 rounded-xl" data-id="element-3547">
                    <div className="flex items-center justify-between mb-2" data-id="element-3548">
                      <div data-id="element-3549">
                        <p className="font-semibold text-slate-900" data-id="element-3550">
                          {vendedor}
                        </p>
                        <p className="text-sm text-slate-500" data-id="element-3551">
                          {data.count} pedidos
                        </p>
                      </div>
                      <p className="text-lg font-bold text-[#2952C4]" data-id="element-3552">
                        {formatCurrency(data.total)}
                      </p>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2" data-id="element-3553">
                      <div className="bg-[#2952C4] h-2 rounded-full transition-all" style={{
                width: `${data.total / Math.max(...Object.values(salesByVendor).map(d => d.total)) * 100}%`
              }} data-id="element-3554" />
                    </div>
                  </div>)}
            </div>
          </Card>}
      </div>
    </div>;
}